import type { WebhookEndpoint } from '@compass/client/types/webhook';
import { WebhookEnvironment } from '@compass/client/types/webhook';
import { useStore } from '@store/index';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { useCreateWebhookEndpoint, useUpdateWebhookEndpoint } from '../hooks/useWebhookEndpoints';
import {
  buildCreateEndpointRequest,
  buildUpdateEndpointRequest,
  CASE_EVENT_TYPES,
  describeEndpointChanges,
  emptyEndpointForm,
  type EndpointFormErrors,
  type EndpointFormState,
  endpointToForm,
  hasFormErrors,
  isConflictError,
  MAX_TIMEOUT_SECONDS,
  MIN_TIMEOUT_SECONDS,
  validateEndpointForm,
  WEBHOOK_TEST_EVENT_TYPE,
  webhookErrorMessage,
} from '../utils';
import { CustomHeadersEditor } from './CustomHeadersEditor';

interface EndpointFormDialogProps {
  organizationId: string;
  /** Present in edit mode. */
  endpoint?: WebhookEndpoint;
  onClose: () => void;
  /** Create mode: called with the one-time signing secret. */
  onCreated?: (endpoint: WebhookEndpoint, signingSecret: string) => void;
}

/**
 * Create or edit an endpoint. Customer endpoints are always scoped to this
 * organization (the server sets the filter from the path), may narrow by
 * program, and can never set a source client.
 */
export function EndpointFormDialog({
  organizationId,
  endpoint,
  onClose,
  onCreated,
}: EndpointFormDialogProps) {
  const isEdit = !!endpoint;
  const [form, setForm] = useState<EndpointFormState>(() =>
    endpoint ? endpointToForm(endpoint) : emptyEndpointForm(),
  );
  const [showErrors, setShowErrors] = useState(false);
  // Edit mode: the changes awaiting confirmation (null while editing).
  const [pendingChanges, setPendingChanges] = useState<string[] | null>(null);

  const createMutation = useCreateWebhookEndpoint(organizationId);
  const updateMutation = useUpdateWebhookEndpoint(organizationId);
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Re-seed when a different endpoint is opened in the same mounted dialog.
  useEffect(() => {
    setForm(endpoint ? endpointToForm(endpoint) : emptyEndpointForm());
    setShowErrors(false);
    setPendingChanges(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint?.id]);

  const { getProgramsArray } = useStore();
  const orgPrograms = getProgramsArray().filter(
    p => !!p.id && (!p.organization_id || p.organization_id === organizationId),
  );
  const programOptions = orgPrograms.map(p => ({ value: p.id as string, label: p.name || p.id! }));
  // Keep narrowing to programs this viewer cannot list, rather than silently dropping it.
  form.programIds.forEach(id => {
    if (!programOptions.some(o => o.value === id)) programOptions.push({ value: id, label: id });
  });

  const errors: EndpointFormErrors = useMemo(() => validateEndpointForm(form), [form]);
  const visibleErrors: EndpointFormErrors = showErrors ? errors : {};

  const set = <K extends keyof EndpointFormState>(key: K, value: EndpointFormState[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleEvent = (value: string, checked: boolean) =>
    set(
      'eventTypes',
      checked ? [...form.eventTypes, value] : form.eventTypes.filter(t => t !== value),
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (hasFormErrors(errors)) return;

    // An edit changes where and what a live endpoint receives: show what is
    // about to change and save only once confirmed.
    if (endpoint) {
      const changes = describeEndpointChanges(endpoint, form);
      if (changes.length === 0) {
        toast.info('No changes to save');
        return;
      }
      setPendingChanges(changes);
      return;
    }

    createMutation.mutate(buildCreateEndpointRequest(organizationId, form), {
      onSuccess: data => {
        onClose();
        // The secret is shown exactly once: never drop it, even if the response
        // somehow lacks the endpoint.
        if (data?.signing_secret) {
          onCreated?.(data.endpoint ?? {}, data.signing_secret);
        } else {
          toast.success('Endpoint created');
        }
      },
      onError: err => {
        toast.error(webhookErrorMessage(err, 'Failed to create the endpoint'));
      },
    });
  };

  const confirmUpdate = () => {
    if (!endpoint) return;
    updateMutation.mutate(buildUpdateEndpointRequest(organizationId, endpoint, form), {
      onSuccess: () => {
        toast.success('Endpoint updated');
        setPendingChanges(null);
        onClose();
      },
      onError: err => {
        setPendingChanges(null);
        if (isConflictError(err)) {
          toast.error('This endpoint was changed by someone else. Reload to see the latest.', {
            action: { label: 'Reload', onClick: onClose },
          });
          return;
        }
        toast.error(webhookErrorMessage(err, 'Failed to update the endpoint'));
      },
    });
  };

  return (
    <>
      <Dialog
        open
        onOpenChange={next => {
          if (!next && !isPending) onClose();
        }}
      >
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit endpoint' : 'Add endpoint'}</DialogTitle>
            <DialogDescription>
              Compass sends signed case events for this organization to the URL below.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 py-2" noValidate>
            <div className="space-y-2">
              <Label htmlFor="webhook-name">Name</Label>
              <Input
                id="webhook-name"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Case sync"
                maxLength={100}
                autoFocus
                disabled={isPending}
              />
              {visibleErrors.name && (
                <p className="text-xs text-destructive">{visibleErrors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhook-description">
                Description <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="webhook-description"
                value={form.description}
                onChange={e => set('description', e.target.value)}
                rows={2}
                maxLength={500}
                disabled={isPending}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
              <div className="space-y-2">
                <Label htmlFor="webhook-url">URL</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  inputMode="url"
                  value={form.url}
                  onChange={e => set('url', e.target.value)}
                  placeholder="https://example.com/webhooks/compass"
                  className="font-mono text-sm"
                  disabled={isPending}
                  autoComplete="off"
                />
                {visibleErrors.url && (
                  <p className="text-xs text-destructive">{visibleErrors.url}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="webhook-environment">Environment</Label>
                <Select
                  value={String(form.environment)}
                  onValueChange={value => set('environment', Number(value) as WebhookEnvironment)}
                  disabled={isPending}
                >
                  <SelectTrigger id="webhook-environment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(WebhookEnvironment.WEBHOOK_ENVIRONMENT_PRODUCTION)}>
                      Production
                    </SelectItem>
                    <SelectItem value={String(WebhookEnvironment.WEBHOOK_ENVIRONMENT_SANDBOX)}>
                      Developer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Events</legend>
              <div className="space-y-2 rounded-md border px-3 py-3">
                {CASE_EVENT_TYPES.map(event => {
                  const id = `webhook-event-${event.value.replace(/\./g, '-')}`;
                  return (
                    <label key={event.value} htmlFor={id} className="flex items-start gap-2">
                      <Checkbox
                        id={id}
                        checked={form.eventTypes.includes(event.value)}
                        onCheckedChange={checked => toggleEvent(event.value, checked === true)}
                        disabled={isPending}
                        className="mt-0.5"
                      />
                      <span className="text-sm">
                        <span className="font-mono">{event.value}</span>
                        <span className="block text-xs text-muted-foreground">
                          {event.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
                <label className="flex items-start gap-2 opacity-70">
                  <Checkbox
                    checked
                    disabled
                    className="mt-0.5"
                    aria-label="Test event (always on)"
                  />
                  <span className="text-sm">
                    <span className="font-mono">{WEBHOOK_TEST_EVENT_TYPE}</span>
                    <span className="block text-xs text-muted-foreground">
                      Always on. Sent only when you click Send test event.
                    </span>
                  </span>
                </label>
              </div>
              {visibleErrors.eventTypes && (
                <p className="text-xs text-destructive">{visibleErrors.eventTypes}</p>
              )}
            </fieldset>

            {programOptions.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Programs <span className="text-muted-foreground">(optional)</span>
                </Label>
                <SearchableMultiSelect
                  options={programOptions}
                  selected={form.programIds}
                  onChange={value => set('programIds', value)}
                  placeholder="All programs"
                  searchPlaceholder="Search programs..."
                  emptyMessage="No programs found."
                  className="w-full px-3"
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to receive events for every program in this organization.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>
                Custom headers <span className="text-muted-foreground">(optional)</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Values are encrypted and never shown again.
                {isEdit && ' Leave a stored value blank to keep it.'}
              </p>
              <CustomHeadersEditor
                rows={form.headers}
                onChange={rows => set('headers', rows)}
                errors={visibleErrors.headers}
                disabled={isPending}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="webhook-timeout">Timeout (seconds)</Label>
                <Input
                  id="webhook-timeout"
                  type="number"
                  min={MIN_TIMEOUT_SECONDS}
                  max={MAX_TIMEOUT_SECONDS}
                  step={1}
                  value={Number.isNaN(form.timeoutSeconds) ? '' : form.timeoutSeconds}
                  onChange={e => set('timeoutSeconds', e.target.valueAsNumber)}
                  disabled={isPending}
                />
                {visibleErrors.timeoutSeconds && (
                  <p className="text-xs text-destructive">{visibleErrors.timeoutSeconds}</p>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2 self-end">
                <div>
                  <Label htmlFor="webhook-omit-ref">Omit referenceId</Label>
                  <p className="text-xs text-muted-foreground">
                    Leave your case reference out of payloads.
                  </p>
                </div>
                <Switch
                  id="webhook-omit-ref"
                  checked={form.omitReferenceId}
                  onCheckedChange={value => set('omitReferenceId', value)}
                  disabled={isPending}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending || (showErrors && hasFormErrors(errors))}
                data-analytics-id={isEdit ? 'webhooks:endpoint:update' : 'webhooks:endpoint:create'}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? 'Save changes' : 'Add endpoint'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {endpoint && pendingChanges && (
        <Dialog
          open
          onOpenChange={next => {
            if (!next && !isPending) setPendingChanges(null);
          }}
        >
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Save changes to {endpoint.name || 'this endpoint'}?</DialogTitle>
              <DialogDescription>
                New deliveries to this endpoint use the updated settings.
              </DialogDescription>
            </DialogHeader>
            <ul className="list-disc space-y-1 pl-5 text-sm break-all" aria-label="Changes">
              {pendingChanges.map(change => (
                <li key={change}>{change}</li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingChanges(null)}
                disabled={isPending}
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={confirmUpdate}
                disabled={isPending}
                data-analytics-id="webhooks:endpoint:update-confirm"
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
